import type { DoctorShift } from '../../types/doctorShift';
import type {
  DoctorShiftRepository,
  FetchDoctorShiftsParams,
  CreateDoctorShiftParams,
  UpdateDoctorShiftParams,
  DeleteDoctorShiftParams,
  OperationResult,
} from '../../application/doctorShift/ports/DoctorShiftRepository';
import { doctorShiftApi } from '../api/doctorShiftApi';
import { AxiosError } from 'axios';

/**
 * HTTPステータスコードからエラーコードを判定
 */
const getErrorCodeFromStatus = (
  status: number
): 'CONFLICT' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'UNKNOWN' => {
  switch (status) {
    case 409:
      return 'CONFLICT';
    case 404:
      return 'NOT_FOUND';
    case 422:
      return 'VALIDATION_ERROR';
    default:
      return 'UNKNOWN';
  }
};

/**
 * エラーメッセージを取得
 */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return '予期しないエラーが発生しました';
};

/**
 * Axiosエラーかどうかを判定
 */
const isAxiosError = (error: unknown): error is AxiosError => {
  return (error as AxiosError).isAxiosError === true;
};

/**
 * 医師シフトリポジトリ実装
 *
 * @description
 * DoctorShiftRepository インターフェースの具象実装。
 * API Clientを使用してバックエンドAPIと通信する。
 *
 * @implements {DoctorShiftRepository}
 */
export class DoctorShiftRepositoryImpl implements DoctorShiftRepository {
  /**
   * 指定期間のシフト一覧を取得する
   *
   * @param {FetchDoctorShiftsParams} params - 取得パラメータ
   * @returns {Promise<DoctorShift[]>} シフト一覧
   */
  async fetchShifts(params: FetchDoctorShiftsParams): Promise<DoctorShift[]> {
    return doctorShiftApi.getShifts({
      startDate: params.startDate,
      endDate: params.endDate,
      doctorId: params.doctorId,
      startTimeFrom: params.startTimeFrom,
      startTimeTo: params.startTimeTo,
    });
  }

  /**
   * シフトを新規作成する
   *
   * @param {CreateDoctorShiftParams} params - 作成パラメータ
   * @returns {Promise<OperationResult<DoctorShift>>} 操作結果
   */
  async createShift(
    params: CreateDoctorShiftParams
  ): Promise<OperationResult<DoctorShift>> {
    try {
      const data = await doctorShiftApi.createShift({
        doctorId: params.doctor_id,
        shiftDate: params.shift_date,
        startTime: params.start_time,
        endTime: params.end_time,
        slotType: params.slot_type,
      });
      return {
        success: true,
        data,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const errorCode = isAxiosError(error)
        ? getErrorCodeFromStatus(error.response?.status || 500)
        : 'UNKNOWN';

      return {
        success: false,
        error: errorMessage,
        errorCode,
      };
    }
  }

  /**
   * シフトを更新する
   *
   * @param {UpdateDoctorShiftParams} params - 更新パラメータ
   * @returns {Promise<OperationResult<DoctorShift>>} 操作結果
   */
  async updateShift(
    params: UpdateDoctorShiftParams
  ): Promise<OperationResult<DoctorShift>> {
    try {
      const data = await doctorShiftApi.updateShift(params.id, {
        doctorId: params.doctor_id,
        shiftDate: params.shift_date,
        startTime: params.start_time,
        endTime: params.end_time,
        slotType: params.slot_type,
        version: params.version,
      });
      return {
        success: true,
        data,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const errorCode = isAxiosError(error)
        ? getErrorCodeFromStatus(error.response?.status || 500)
        : 'UNKNOWN';

      // 競合エラーの場合のカスタムメッセージ
      if (errorCode === 'CONFLICT') {
        return {
          success: false,
          error:
            '他のユーザーがこのシフトを編集中です。ページを更新して最新のデータを取得してください。',
          errorCode,
        };
      }

      return {
        success: false,
        error: errorMessage,
        errorCode,
      };
    }
  }

  /**
   * シフトを削除する
   *
   * @param {DeleteDoctorShiftParams} params - 削除パラメータ
   * @returns {Promise<OperationResult>} 操作結果
   */
  async deleteShift(params: DeleteDoctorShiftParams): Promise<OperationResult> {
    try {
      await doctorShiftApi.deleteShift(params.id, params.version);
      return {
        success: true,
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      const errorCode = isAxiosError(error)
        ? getErrorCodeFromStatus(error.response?.status || 500)
        : 'UNKNOWN';

      // 競合エラーの場合のカスタムメッセージ
      if (errorCode === 'CONFLICT') {
        return {
          success: false,
          error:
            '他のユーザーがこのシフトを編集中です。ページを更新して最新のデータを取得してください。',
          errorCode,
        };
      }

      // Not Foundの場合のカスタムメッセージ
      if (errorCode === 'NOT_FOUND') {
        return {
          success: false,
          error: '指定されたシフトが見つかりませんでした。',
          errorCode,
        };
      }

      return {
        success: false,
        error: errorMessage,
        errorCode,
      };
    }
  }
}

/**
 * シングルトンインスタンス
 */
export const doctorShiftRepository = new DoctorShiftRepositoryImpl();
